import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ProtectedRoute } from './components/Common/ProtectedRoute';
import { useOfferNotifications } from './hooks/useOfferNotifications';
import AuthPage from './pages/AuthPage';
import Home from './pages/Home';
import Dashboard from './pages/Dashboard';
import RoomsBid from './pages/RoomsBid';
import RoomsSecondHand from './pages/RoomsSecondHand';
import Ecommerce from './pages/Ecommerce';
import History from './pages/History';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import Unauthorized from './pages/Unauthorized';
import AuctionRoomManagement from './pages/AuctionRoomManagement';
import ResetPasswordForm from './components/Auth/ResetPasswordForm';
import AppPreloader from './components/Preloader/AppPreloader';
import NotFoundPage from './components/NotFoundPage';
import './i18n/i18n';
import './App.css';
import AdminDashboard from './pages/Admin/AdminDashboard';
import ProductDetails from './pages/ProductDetails';
import Payment from './pages/Payment';
import AuctionConfirmation from './pages/AuctionConfirmation';
import LiveAuctionRoom from './components/Auction/LiveAuctionRoom';
import MyOffersPage from './pages/MyOffersPage';
import ReceivedOffersPage from './pages/ReceivedOffersPage';
import RecoveryWalletPage from './pages/RecoveryWalletPage';
import WalletTopUp from './pages/WalletTopUp';
import OfferDetails from './pages/OfferDetails';
import NotificationsPage from './pages/NotificationsPage';
import ChatContactsBubble from './components/Chat/ChatContactsBubble';
import ChatPage from './pages/ChatPage';

// Public Route Component (redirect to home if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <AppPreloader />;
  }
  
  return isAuthenticated ? <Navigate to="/" /> : children;
};

// App Content Component
const AppContent = () => {
  // Initialize offer notifications
  useOfferNotifications();

  return (
    <Routes>
      <Route 
        path="/auth" 
        element={
          <PublicRoute>
            <AuthPage />
          </PublicRoute>
        } 
      />
      <Route 
        path="/reset-password" 
        element={
          <PublicRoute>
            <ResetPasswordForm />
          </PublicRoute>
        } 
      />
      <Route 
        path="/" 
        element={<Home />} 
      />
      <Route 
        path="/home" 
        element={<Home />} 
      />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/rooms/bid" 
        element={<RoomsBid />} 
      />
      <Route 
        path="/auction-room/:roomId" 
        element={<LiveAuctionRoom />} 
      />
      <Route 
        path="/rooms/second-hand" 
        element={
          <ProtectedRoute requiredPermissions={['view_products']}>
            <RoomsSecondHand />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/ecommerce" 
        element={<Ecommerce />} 
      />
      <Route 
        path="/ecommerce/product/:productId" 
        element={<ProductDetails />} 
      />

      <Route 
        path="/auction-rooms/manage" 
        element={
          <ProtectedRoute requiredPermissions={['manage_auctions']}>
            <AuctionRoomManagement />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/history" 
        element={
          <ProtectedRoute>
            <History />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/profile" 
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/recovery-wallet" 
        element={
          <ProtectedRoute>
            <RecoveryWalletPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/wallet/topup" 
        element={
          <ProtectedRoute>
            <WalletTopUp />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/offers/my-offers" 
        element={
          <ProtectedRoute>
            <MyOffersPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/offers/received" 
        element={
          <ProtectedRoute>
            <ReceivedOffersPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/offers/:offerId" 
        element={
          <ProtectedRoute>
            <OfferDetails />
          </ProtectedRoute>
        } 
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <ChatPage />
          </ProtectedRoute>
        }
      />
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute requiredPermissions={['system_admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/unauthorized" 
        element={<Unauthorized />} 
      />
      <Route 
        path="/payment/:productId" 
        element={
          <ProtectedRoute requiredPermissions={['view_products']}>
            <Payment />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/auction/confirm/:token" 
        element={<AuctionConfirmation />} 
      />
      <Route 
        path="*" 
        element={<NotFoundPage />} 
      />
    </Routes>
  );
};

// Wrapper to give ChatContactsBubble access to navigate
const ChatBubbleWithNav = () => {
  const navigate = useNavigate();
  return <ChatContactsBubble onOpenChat={() => navigate('/chat')} />;
};

// Main App Component
function App() {
  return (
    <Router>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <NotificationProvider>
              <AppContent />
              <ChatBubbleWithNav />
            </NotificationProvider>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;
